import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type SyntheticEvent,
} from "react";

/**
 * CR-112 — type-to-confirm gate for destructive batch buttons.
 *
 * Shares the `.confirm-dialog` shell with the simple ConfirmDialog (backdrop,
 * radius, typography) and layers a phrase input on top. The Confirm button
 * stays disabled until the input value strictly equals `phrase` (case-sensitive,
 * no trim — the friction is the whole point). Enter submits when the phrase
 * is valid; Escape cancels via the native <dialog> cancel event.
 *
 * Decisions carried from Gemini Pass 1:
 *   - strict match (no toLowerCase/trim) — the accidental-click guardrail
 *   - phrase rendered in <strong> so the operator never has to guess casing
 */
export interface TypeToConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly message: string;
  readonly phrase: string;
  readonly confirmLabel: string;
  readonly confirmVariant?: "primary" | "danger";
  onConfirm(): void;
  onCancel(): void;
}

export function TypeToConfirmDialog({
  open,
  title,
  message,
  phrase,
  confirmLabel,
  confirmVariant = "primary",
  onConfirm,
  onCancel,
}: TypeToConfirmDialogProps): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const messageId = useId();
  const hintId = useId();
  const [value, setValue] = useState("");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
    }
    if (!open && el.open) {
      el.close();
    }
  }, [open]);

  // Reset the input on each open → close → open cycle so a previous
  // correct phrase doesn't pre-arm the Confirm button.
  useEffect(() => {
    if (open) {
      setValue("");
      // Give the native dialog a tick to render before focusing.
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [open]);

  const confirmClass =
    confirmVariant === "danger" ? "btn btn--danger" : "btn btn--primary";

  const matches = value === phrase;

  const handleNativeCancel = (event: SyntheticEvent<HTMLDialogElement>): void => {
    event.preventDefault();
    onCancel();
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter" && matches) {
      event.preventDefault();
      onConfirm();
    }
  };

  return (
    <dialog
      ref={ref}
      className="confirm-dialog"
      role={confirmVariant === "danger" ? "alertdialog" : "dialog"}
      aria-labelledby={titleId}
      aria-describedby={messageId}
      onCancel={handleNativeCancel}
    >
      <div className="confirm-dialog__content">
        <h2 id={titleId} className="confirm-dialog__title">
          {title}
        </h2>
        <p id={messageId} className="confirm-dialog__message">
          {message}
        </p>
        <p
          id={hintId}
          className="confirm-dialog__phrase-hint"
        >
          Type <strong>{phrase}</strong> to confirm.
        </p>
        <input
          ref={inputRef}
          type="text"
          className="confirm-dialog__phrase-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-describedby={hintId}
          aria-label={`Type ${phrase} to confirm`}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          data-testid="type-to-confirm-input"
        />
        <div className="confirm-dialog__actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={confirmClass}
            onClick={onConfirm}
            disabled={!matches}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </dialog>
  );
}
